import React, {useCallback, useEffect, useMemo, useState} from "react";
import SearchInput from "../../../../../components/base/input/search_input";
import PartnerDataGrid, {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../../containers/data-grid";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import {MissingProductsListDataGridColumnNames} from "../../../../../../core/constants/table/data-grid-column-keys";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {createUUId} from "../../../../../../core/services/utils/utils";
import Button from "../../../../../components/base/button/button";
import useSearch from "../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import ProductInformationPopover from "../../../../../components/app-specific/draggable-popover/product-information-popover";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";


const dataGridSaveConfig = {
    name: 'missing-public-products-data-grid',
    version: 1,
}


const MissingPublicProductsManagementView = () => {
    const [importing, setImporting] = useState([]);
    const [data, setData] = useState([])
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('damage-control-missing-products-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.damageControlMissingProducts);
        configureBehavioralTool((tags) => tags.damageControlMissingProducts);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Part No",
                name: MissingProductsListDataGridColumnNames.partNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Category",
                name: MissingProductsListDataGridColumnNames.category,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                name: MissingProductsListDataGridColumnNames.import,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.right,
                sortable: false,
                pinnedToggleable: true,
                resizable: false,
                visibilityToggleable: false,
            },
        ], [])

    const [dataGridColumns, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });


    /**
     * Searches among the entries based on the filters from the user.
     *
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = {
            ...args.paginationInfo,
            keyword: args.filters?.keyword,
        }
        // TODO: call the api for fetching missing products list
        const response = {
            resultFlag: true,
            data: {
                total: 10,
                items: Array.from({length: 10}).map((_, i) => ({
                    id: i,
                    partNo: `Part No ${i}`,
                    mainCategoryTitle: `Category ${i}`,
                })),
            }
        }
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data?.items?.map(e => ({
                ...e,
                id: createUUId(true),
            })) ?? []);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.total ?? 0,
            }))
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
    }), [initialPaginationInfo]);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Changes the keywords, and finally sets the page to 1 so that the useEffect watcher of the paginationInfo
     * can call the api with the new data
     * @param keywords {string}
     * @type {(function(*): void)}
     */
    const searchByKeyword = useCallback((keywords) => {
        searchData.setFilters?.(prevState => ({...prevState, keyword: keywords}))
    }, [searchData])

    /**
     * Imports the product using the api
     * @type {(function(*): Promise<void>)}
     */
    const importProduct = useCallback(async (product) => {
        const list = [];
        if (!product) {
            // means we are importing all
            searchData.setLoading(true);
            list.push(...data.map(e => e.partNo));
        } else {
            setImporting(prevState => [...new Set([...prevState, product.id])]);
            list.push(product.partNo);
        }
        // TODO: call the api for importing the product
        const response = {
            resultFlag: true,
        }
        if (!isMounted())
            return;
        if (!product) {
            searchData.setLoading(false);
        } else {
            setImporting(prevState => prevState.filter(e => e !== product.id));
        }
        if (response?.resultFlag) {
            if (!product) {
                setData([]);
            } else {
                setData(prevState => prevState.filter(e => e.id !== product.id));
            }
        }
    }, [data, isMounted, searchData]);


    /**
     * Memo version of table row records used in data grid
     */
    const rows = useMemo(() => {
        return data?.map(item => ({
            key: item.id,
            data: item,
            cells: {
                [MissingProductsListDataGridColumnNames.partNo]:
                    <ProductInformationPopover partNo={item?.partNo}>
                        {item?.partNo ?? ''}
                    </ProductInformationPopover>,
                [MissingProductsListDataGridColumnNames.category]: item?.mainCategoryTitle,
                [MissingProductsListDataGridColumnNames.import]:
                    <Button
                        disabled={importing.includes(item.id)}
                        variant={'outlined'}
                        color={'primary'}
                        onClick={() => importProduct(item)}
                    >
                        {
                            importing.includes(item.id)
                                ? "Importing..."
                                : "Import"
                        }
                    </Button>
            }
        })) ?? []
    }, [importProduct, importing, data])


    return (
        <>
            <div className={'missing-products-view'}>
                <div className={'d-flex align-items-center justify-content-between mb-2'}>
                    <p className={'title'}>
                        Missing Public Products
                    </p>
                    {
                        !searchData.loading && data.length > 0 &&
                        <Button
                            disabled={importing.length === data.length}
                            variant={'outlined'}
                            color={'primary'}
                            onClick={() => importProduct()}
                        >
                            Import All Products
                        </Button>
                    }
                </div>
                <p className={'description'}>
                    This page lists all the products that are visible to all your and your partners' customers but you
                    do not have then
                    available in your inventory.
                </p>
                <div className='w-100 my-5'/>
                <SearchInput
                    loading={searchData.loading}
                    autoSearch
                    immediateSearch
                    search={searchByKeyword}
                    initialValue={searchData.filters?.keyword ?? undefined}
                    label={'Search'}
                />
                <div className='w-100 my-2'/>
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    rows={rows}
                    hideFooterActions
                    hideExporting
                    state={{
                        pagination: searchData.paginationInfo,
                        loading: {state: searchData.loading},
                        density: dataGridDensity,
                    }}
                    onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                />
            </div>
        </>
    )
}

export default MissingPublicProductsManagementView;
